import React, { useState } from 'react';
import { Button, Card } from '../ui/BaseComponents';
import { toInternalFormatArray } from '../../utils/questionSchemaConverter';

/**
 * ImportModal - Modal für Import von Fragen oder kompletten Templates
 * 
 * @param {Object} props
 * @param {Function} props.onClose - Callback zum Schließen
 * @param {Function} props.onImportQuestions - Callback für Fragen-Import (questions, mode)
 * @param {Function} props.onImportTemplate - Callback für kompletten Template-Import
 */
function ImportModal({ onClose, onImportQuestions, onImportTemplate }) {
  const [importType, setImportType] = useState('questions'); // 'questions' oder 'template'
  const [importMode, setImportMode] = useState('add'); // 'add' oder 'replace'
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setJsonText(event.target.result);
      parseAndPreview(event.target.result);
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      setError('Nur JSON-Dateien sind erlaubt');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setJsonText(event.target.result);
      parseAndPreview(event.target.result);
    };
    reader.readAsText(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const parseAndPreview = (text) => {
    try {
      const data = JSON.parse(text);
      setError('');

      // Prüfen ob es ein komplettes Template oder nur Fragen sind
      if (data.questions && Array.isArray(data.questions)) {
        // Komplettes Template
        setPreview({
          type: 'template',
          name: data.name || 'Unbenanntes Template',
          description: data.description || '',
          courseId: data.courseId || null,
          questionCount: data.questions.length,
          hasSettings: !!data.settings,
          questions: data.questions,
          settings: data.settings
        });
        setImportType('template');
      } else if (Array.isArray(data)) {
        // Nur Fragen-Array
        setPreview({
          type: 'questions',
          questionCount: data.length,
          questions: data
        });
        setImportType('questions');
      } else {
        setError('Ungültiges Format: Erwarte Array von Fragen oder Template-Objekt');
        setPreview(null);
      }
    } catch (err) {
      setError('Fehler beim Parsen des JSON: ' + err.message);
      setPreview(null);
    }
  };

  const handleImport = () => {
    if (!preview) {
      setError('Keine gültige Vorschau verfügbar');
      return;
    }

    if (preview.type === 'template') {
      // Komplettes Template importieren - Questions konvertieren
      const convertedQuestions = toInternalFormatArray(preview.questions);
      onImportTemplate({
        name: preview.name,
        description: preview.description,
        courseId: preview.courseId,
        questions: convertedQuestions,
        settings: preview.settings
      });
    } else {
      // Nur Fragen importieren - Questions konvertieren
      const convertedQuestions = toInternalFormatArray(preview.questions);
      onImportQuestions(convertedQuestions, importMode);
    }

    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-800">
              📤 Template/Fragen importieren
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Drag & Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById('file-upload').click()}
            className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
              isDragging 
                ? 'border-blue-500 bg-blue-50 scale-105' 
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }`}
          >
            <input
              id="file-upload"
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="space-y-3">
              <div>
                <p className="text-lg font-semibold text-gray-700">
                  {isDragging ? 'Datei hier ablegen...' : 'Datei hierher ziehen oder klicken'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Unterstützte Formate: .json
                </p>
              </div>
            </div>
          </div>

          {/* JSON-Text-Eingabe */}
          <Card>
            <h3 className="font-semibold text-gray-800 mb-3">✍️ Oder JSON-Text einfügen</h3>
            <textarea
              value={jsonText}
              onChange={(e) => {
                setJsonText(e.target.value);
                parseAndPreview(e.target.value);
              }}
              className="w-full h-48 p-3 border border-gray-300 rounded-lg font-mono text-sm"
              placeholder='{"name": "Mein Quiz", "questions": [...]}'
            />
            {error && (
              <div className="mt-2 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
                ⚠️ {error}
              </div>
            )}
          </Card>

          {/* Vorschau */}
          {preview && (
            <Card className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-300">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">✅</span>
                <h3 className="font-semibold text-gray-800">Vorschau</h3>
              </div>

              {preview.type === 'template' ? (
                <>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between p-2 bg-white rounded">
                      <span className="text-sm text-gray-600">Template-Name:</span>
                      <span className="font-semibold">{preview.name}</span>
                    </div>
                    {preview.description && (
                      <div className="flex items-center justify-between p-2 bg-white rounded">
                        <span className="text-sm text-gray-600">Beschreibung:</span>
                        <span className="font-semibold">{preview.description}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between p-2 bg-white rounded">
                      <span className="text-sm text-gray-600">Fragen:</span>
                      <span className="font-semibold">{preview.questionCount}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-white rounded">
                      <span className="text-sm text-gray-600">Settings enthalten:</span>
                      <span className="font-semibold">{preview.hasSettings ? '✅ Ja' : '❌ Nein'}</span>
                    </div>
                  </div>

                  <div className="p-3 bg-blue-100 border border-blue-300 rounded-lg text-sm text-blue-800">
                    <strong>📋 Komplettes Template:</strong> Name, Beschreibung, alle Fragen und Settings werden importiert. Dein aktuelles Template wird vollständig ersetzt.
                  </div>
                </>
              ) : (
                <>
                  <div className="p-2 bg-white rounded mb-3">
                    <span className="text-sm text-gray-600">Anzahl Fragen: </span>
                    <span className="font-semibold">{preview.questionCount}</span>
                  </div>

                  {/* Import-Modus auswählen */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Import-Modus:
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setImportMode('add')}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          importMode === 'add'
                            ? 'border-green-500 bg-green-100'
                            : 'border-gray-300 hover:border-green-300'
                        }`}
                      >
                        <div className="font-semibold text-sm">➕ Ergänzen</div>
                        <div className="text-xs text-gray-600 mt-1">
                          Fragen zu bestehenden hinzufügen
                        </div>
                      </button>

                      <button
                        onClick={() => setImportMode('replace')}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          importMode === 'replace'
                            ? 'border-orange-500 bg-orange-100'
                            : 'border-gray-300 hover:border-orange-300'
                        }`}
                      >
                        <div className="font-semibold text-sm">Ersetzen</div>
                        <div className="text-xs text-gray-600 mt-1">
                          Alle bisherigen Fragen ersetzen
                        </div>
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Erste 3 Fragen als Vorschau */}
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Fragen-Vorschau (erste {Math.min(3, preview.questionCount)}):
                </h4>
                <div className="space-y-2">
                  {preview.questions.slice(0, 3).map((q, idx) => (
                    <div key={idx} className="p-2 bg-white rounded text-sm">
                      <div className="font-medium">{idx + 1}. {q.question || 'Keine Frage'}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {q.options?.length || 0} Optionen
                      </div>
                    </div>
                  ))}
                  {preview.questionCount > 3 && (
                    <div className="text-xs text-gray-500 text-center">
                      ... und {preview.questionCount - 3} weitere
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={!preview}
          >
            {preview?.type === 'template' ? '📥 Template importieren' : '📥 Fragen importieren'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ImportModal;
